/**
 * Interval Tracking Test Utilities
 * Bu dosya WebView ve normal mod arasındaki interval tracking farklarını test etmek için kullanılır
 */

import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { useTestStore } from '../store/useTestStore';

export interface IntervalTestResult {
  mode: 'normal' | 'webview';
  questionId: string;
  sessionCount: number;
  totalDuration: number;
  intervals: any[];
  hasActiveSession: boolean;
  memoryUsage?: number;
  errors: string[];
}

export class IntervalTrackingTester {
  private results: IntervalTestResult[] = [];
  private startTime: number = Date.now();

  /**
   * Test interval tracking in current mode
   */
  async testCurrentMode(questionId: string, mode: 'normal' | 'webview'): Promise<IntervalTestResult> {
    const errors: string[] = [];
    let sessionCount = 0;
    let totalDuration = 0;
    let intervals: any[] = [];
    let hasActiveSession = false;

    try {
      // Get question data from cache
      const cache = useWhiteboardStore.getState().questionDataCache;
      const questionData = cache.get(questionId);

      if (questionData?.trajectory) {
        sessionCount = questionData.trajectory.length;
        
        // Calculate total duration
        totalDuration = questionData.trajectory.reduce((total, session) => {
          if (session.duration) {
            return total + session.duration;
          } else if (session.endDate && session.startDate) {
            return total + (session.endDate - session.startDate) / 1000;
          } else if (!session.endDate && session.startDate) {
            hasActiveSession = true;
            return total + (Date.now() - session.startDate) / 1000;
          }
          return total;
        }, 0);

        // Format intervals
        intervals = questionData.trajectory.map((session, index) => {
          const isLastSession = index === questionData.trajectory.length - 1;
          const endTime = session.endDate || (isLastSession ? Date.now() : null);
          const duration = session.duration || (endTime && session.startDate ? (endTime - session.startDate) / 1000 : null);
          
          return {
            start: new Date(session.startDate).toISOString().replace('T', ' ').substring(0, 19),
            end: endTime ? new Date(endTime).toISOString().replace('T', ' ').substring(0, 19) : null,
            duration: duration ? Math.round(duration * 100) / 100 : null,
            isActive: !session.endDate
          };
        });
      } else {
        errors.push('No trajectory data found for question');
      }

      // Memory usage check (approximate)
      const memoryUsage = this.estimateMemoryUsage();

    } catch (error) {
      errors.push(`Test error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    const result: IntervalTestResult = {
      mode,
      questionId,
      sessionCount,
      totalDuration: Math.round(totalDuration * 100) / 100,
      intervals,
      hasActiveSession,
      memoryUsage: this.estimateMemoryUsage(),
      errors
    };

    this.results.push(result);
    return result;
  }

  /**
   * Compare normal mode vs WebView mode
   */
  async compareModesForQuestion(questionId: string): Promise<{
    normal: IntervalTestResult;
    webview: IntervalTestResult;
    differences: string[];
  }> {
    // Simulate normal mode test
    const normalResult = await this.testCurrentMode(questionId, 'normal');
    
    // Simulate WebView mode test
    const webviewResult = await this.testCurrentMode(questionId, 'webview');

    const differences: string[] = [];

    if (normalResult.sessionCount !== webviewResult.sessionCount) {
      differences.push(`Session count differs: Normal(${normalResult.sessionCount}) vs WebView(${webviewResult.sessionCount})`);
    }

    if (Math.abs(normalResult.totalDuration - webviewResult.totalDuration) > 0.1) {
      differences.push(`Total duration differs: Normal(${normalResult.totalDuration}s) vs WebView(${webviewResult.totalDuration}s)`);
    }

    if (normalResult.hasActiveSession !== webviewResult.hasActiveSession) {
      differences.push(`Active session state differs: Normal(${normalResult.hasActiveSession}) vs WebView(${webviewResult.hasActiveSession})`);
    }

    return {
      normal: normalResult,
      webview: webviewResult,
      differences
    };
  }

  /**
   * Test session lifecycle
   */
  async testSessionLifecycle(questionId: string): Promise<{
    sessionStarted: boolean;
    sessionEnded: boolean;
    durationCalculated: boolean;
    dataPersistedToCache: boolean;
    errors: string[];
  }> {
    const errors: string[] = [];
    let sessionStarted = false;
    let sessionEnded = false;
    let durationCalculated = false;
    let dataPersistedToCache = false;

    try {
      // Check if session can be started
      const cache = useWhiteboardStore.getState().questionDataCache;
      const initialData = cache.get(questionId);
      
      // Simulate session start
      const mockSession = {
        startDate: Date.now(),
        endDate: undefined,
        duration: undefined
      };

      const trajectory = initialData?.trajectory || [];
      trajectory.push(mockSession);

      sessionStarted = true;

      // Simulate some time passing
      await new Promise(resolve => setTimeout(resolve, 100));

      // Simulate session end
      const endTime = Date.now();
      const duration = (endTime - mockSession.startDate) / 1000;
      
      // Update the trajectory with ended session
      trajectory[trajectory.length - 1] = {
        ...mockSession,
        endDate: endTime,
        duration: duration
      };
      
      sessionEnded = true;
      durationCalculated = duration > 0;

      // Check if data can be persisted
      const updatedData = {
        questionId,
        userId: 'test',
        tenantId: 'test',
        testId: 'test',
        trajectory,
        updatedAt: Date.now(),
        totalTime: duration,
        selectionHistory: '',
        eliminatedChoices: ''
      };

      const newCache = new Map(cache);
      newCache.set(questionId, updatedData);
      
      dataPersistedToCache = true;

    } catch (error) {
      errors.push(`Session lifecycle error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
      sessionStarted,
      sessionEnded,
      durationCalculated,
      dataPersistedToCache,
      errors
    };
  }

  /**
   * Estimate memory usage (approximate)
   */
  private estimateMemoryUsage(): number {
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      return (performance as any).memory.usedJSHeapSize;
    }
    return 0;
  }

  /**
   * Generate test report
   */
  generateReport(): string {
    const report = [
      '# Interval Tracking Test Report',
      `Generated at: ${new Date().toISOString()}`,
      `Test duration: ${((Date.now() - this.startTime) / 1000).toFixed(2)}s`,
      '',
      '## Test Results',
      ''
    ];

    this.results.forEach((result, index) => {
      report.push(`### Test ${index + 1}: ${result.mode.toUpperCase()} Mode`);
      report.push(`- Question ID: ${result.questionId}`);
      report.push(`- Session Count: ${result.sessionCount}`);
      report.push(`- Total Duration: ${result.totalDuration}s`);
      report.push(`- Has Active Session: ${result.hasActiveSession}`);
      report.push(`- Memory Usage: ${result.memoryUsage || 'N/A'} bytes`);
      
      if (result.errors.length > 0) {
        report.push(`- Errors: ${result.errors.join(', ')}`);
      }
      
      report.push('');
    });

    return report.join('\n');
  }

  /**
   * Clear test results
   */
  clearResults(): void {
    this.results = [];
    this.startTime = Date.now();
  }
}

// Export singleton instance
export const intervalTrackingTester = new IntervalTrackingTester();

// Helper function to run quick test
export const runQuickIntervalTest = async (questionId: string) => {
  console.log('🧪 Running quick interval tracking test...');
  
  const result = await intervalTrackingTester.testCurrentMode(questionId, 'webview');
  
  console.log('📊 Test Results:', {
    questionId: result.questionId,
    sessionCount: result.sessionCount,
    totalDuration: result.totalDuration,
    hasActiveSession: result.hasActiveSession,
    intervalsCount: result.intervals.length,
    errors: result.errors
  });

  if (result.errors.length > 0) {
    console.error('❌ Test Errors:', result.errors);
  }

  return result;
};